/**
 * Settings → "Show partner offers" (REQ-025 AC-008).
 *
 * Off hides every insurer offer wherever offers appear — the server returns
 * none — and changes nothing else.
 */
import {
  getMarketplaceHidden,
  setMarketplaceHidden,
} from "@/services/api/marketplace";
import { useI18nStore } from "@/stores/i18nStore";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Tag } from "lucide-react";
import React from "react";
import { toast } from "react-hot-toast";

const QUERY_KEY = ["marketplace", "hidden"];

export const PartnerOffersSettingsCard: React.FC = () => {
  const { t } = useI18nStore();
  const queryClient = useQueryClient();
  const { data: hidden } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getMarketplaceHidden,
  });

  const save = useMutation({
    mutationFn: setMarketplaceHidden,
    onSuccess: (_result, nextHidden) => {
      queryClient.setQueryData(QUERY_KEY, nextHidden);
      void queryClient.invalidateQueries({
        queryKey: ["marketplace", "offers"],
      });
      toast.success(t.marketplaceSettingSaved);
    },
    onError: () => toast.error(t.marketplaceSettingFailed),
  });

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center space-x-2">
            <Tag className="w-5 h-5 text-gray-600" />
            <span>{t.marketplaceSettingTitle}</span>
          </h2>
          <p className="text-sm text-gray-600">{t.marketplaceSettingHint}</p>
        </div>
        <label
          htmlFor="partner-offers-toggle"
          className="relative mt-1 inline-flex shrink-0 cursor-pointer items-center"
          aria-label={t.marketplaceSettingTitle}
        >
          <input
            id="partner-offers-toggle"
            type="checkbox"
            checked={hidden === false}
            disabled={hidden === undefined || save.isPending}
            onChange={(event) => save.mutate(!event.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-600"></div>
        </label>
      </div>
    </div>
  );
};
